import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useEmployee } from '@/lib/pulse/useEmployee';
import { supabase } from '@/lib/supabase';
import { resolvePlatformAdmin, platformAdminCache } from '@/lib/platform/usePlatformAdmin';
import { Loader2 } from 'lucide-react';

/**
 * Auth landing route. Handles three arrival modes:
 *  1. OTP-code typed in SignIn.tsx → session already live, just route.
 *  2. Magic-link click, implicit flow → #access_token=&refresh_token=&type=…
 *     → setSession().
 *  3. Legacy PKCE link still in flight → ?code=<jwt>
 *     → exchangeCodeForSession() (only succeeds on originating browser).
 * After consumption, tokens are stripped from the URL via history.replaceState.
 */
export default function Callback() {
  const navigate = useNavigate();
  const { user, isLoading } = useAuth();
  const employee = useEmployee();
  const [exchanging, setExchanging] = useState(true);
  const [exchangeError, setExchangeError] = useState<string | null>(null);

  // One-shot URL-credential consumption. Runs before any routing decisions.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = new URL(window.location.href);
        const code = url.searchParams.get('code');
        const hash = window.location.hash.startsWith('#')
          ? window.location.hash.slice(1)
          : '';
        const hp = new URLSearchParams(hash);
        const access_token = hp.get('access_token');
        const refresh_token = hp.get('refresh_token');

        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            console.error('[auth/callback] exchangeCodeForSession failed', error);
            if (!cancelled) setExchangeError(error.message);
          }
        } else if (access_token && refresh_token) {
          const { error } = await supabase.auth.setSession({
            access_token,
            refresh_token,
          });
          if (error) {
            console.error('[auth/callback] setSession failed', error);
            if (!cancelled) setExchangeError(error.message);
          }
        }

        // Strip credentials from the address bar regardless of outcome.
        if (code || access_token || refresh_token) {
          window.history.replaceState({}, '', url.pathname);
        }
      } catch (e) {
        console.error('[auth/callback] unexpected error', e);
        if (!cancelled) setExchangeError((e as Error).message);
      } finally {
        if (!cancelled) setExchanging(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const [isPlatformAdmin, setIsPlatformAdmin] = useState<boolean | null>(null);

  useEffect(() => {
    if (!user?.id) {
      setIsPlatformAdmin(null);
      return;
    }
    if (platformAdminCache.has(user.id)) {
      setIsPlatformAdmin(platformAdminCache.get(user.id)!);
      return;
    }
    let cancelled = false;
    resolvePlatformAdmin(user.id).then((ok) => {
      if (!cancelled) setIsPlatformAdmin(ok);
    });
    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    if (exchanging) return;
    if (isLoading) return;
    if (!user) {
      navigate('/auth/signin', { replace: true });
      return;
    }
    // Wait for platform-admin resolution before making routing decisions —
    // platform admins have no pulse_employees row and would otherwise be
    // bounced to /auth/no-invite.
    if (isPlatformAdmin === null) return;

    const next = new URLSearchParams(window.location.search).get('next');
    // Platform admins skip the employee gate entirely.
    if (isPlatformAdmin) {
      const dest = next ? `/redirect?next=${encodeURIComponent(next)}` : '/redirect';
      navigate(dest, { replace: true });
      return;
    }
    // Admin-claim flow has its own invite table (org-admin invites live
    // outside pulse_pending_invitations, so bootstrap-employee returns
    // no_invite for first-time tenant admins). Honor next=/auth/claim
    // before the no_invite bounce so they can reach claim_first_admin.
    const isClaimNext = !!next && next.startsWith('/auth/claim');
    if (employee.status === 'no_invite' && !isClaimNext) {
      navigate('/auth/no-invite', { replace: true });
      return;
    }
    if (employee.status === 'ready' || isClaimNext) {
      const dest = next ? `/redirect?next=${encodeURIComponent(next)}` : '/redirect';
      navigate(dest, { replace: true });
    }
  }, [exchanging, user, isLoading, employee, navigate, isPlatformAdmin]);

  return (
    <main className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-3 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
        <p className="text-sm">Signing you in…</p>
        {exchangeError && (
          <p className="text-sm text-destructive">{exchangeError}</p>
        )}
        {employee.status === 'error' && (
          <p className="text-sm text-destructive">{employee.message}</p>
        )}
      </div>
    </main>
  );
}
